<script setup lang="ts">
import { computed, watch } from "vue";

import { CloseMenu } from "@/features/reset-trade-in/ui";
import { logger } from "@/shared/lib";
import { useApiGetWidgetData, useGlobalStore } from "@/shared/model";

import WidgetSkeleton from "./WidgetSkeleton.vue";

const globalStore = useGlobalStore();
const { isPending, error } = useApiGetWidgetData();

/** The last screen has its own "Close" and "Send another request" buttons */
const isCloseMenuShown = computed(() => !globalStore.isCurrentStep("thanksForOrder"));

// The popup covers the partner page, so the page itself must not scroll under it
watch(
    () => globalStore.modeIsPopup && globalStore.isOpen,
    (isLocked, _, onCleanup) => {
        if (!isLocked) return;

        const { overflow } = document.body.style;
        document.body.style.overflow = "hidden";
        onCleanup(() => (document.body.style.overflow = overflow));
    },
    { immediate: true },
);

watch(error, (value) => value && logger.error("widget data is not loaded", value));

function showInTopLayer(element: unknown): void {
    if (element instanceof HTMLElement && element.popover && !element.matches(":popover-open")) {
        element.showPopover();
    }
}
</script>

<template>
    <Transition name="container">
        <aside
            v-if="globalStore.isOpen"
            :ref="showInTopLayer"
            :class="['container', { container_popup: globalStore.modeIsPopup }]"
            :popover="globalStore.modeIsPopup ? 'manual' : undefined"
        >
            <div
                v-if="globalStore.modeIsPopup"
                class="container__backdrop"
                @click="globalStore.closeWidget"
            />

            <div class="container__wrapper">
                <CloseMenu v-if="isCloseMenuShown" />

                <WidgetSkeleton v-if="isPending" />
                <slot v-else />
            </div>
        </aside>
    </Transition>
</template>

<style scoped>
.container_popup {
    /* Drops the browser [popover] styles, the overlay is styled from scratch below.
       Must stay first: it resets every property declared before it */
    all: unset;
    position: fixed;
    inset: 0;
    display: flex;
    padding: 16px;
    box-sizing: border-box;
    /* The whole overlay scrolls, not the card: dropdowns can go past the card edge onto the backdrop */
    overflow-y: auto;
    overscroll-behavior: contain;
}

/* Fixed, so it still covers the screen when the overlay is scrolled */
.container__backdrop {
    position: fixed;
    inset: 0;
    background-color: rgb(0 0 0 / 50%);
}

.container__wrapper {
    position: relative;
    width: 100%;
    max-width: 448px;
    min-height: 200px;
    margin: auto;
    background-color: #fff;
    border-radius: 20px;
}

/* On mobile the popup becomes a full-height bottom sheet */
@media screen and (max-width: 767px) {
    .container_popup {
        padding: 24px 0 0;
    }

    .container_popup .container__wrapper {
        --step-min-height: calc(100dvh - 24px);

        max-width: none;
        margin: auto 0 0;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }
}

.container-enter-active,
.container-leave-active {
    transition: opacity 150ms ease;
}

.container-enter-from,
.container-leave-to {
    opacity: 0;
}
</style>
